import { FunctionComponent, UIEvent, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Flex, Skeleton, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// services
import { isRTL } from 'core/services/language';
import NotificationService from 'core/services/notification';
import { onGetNotifications, onMakrkAllNotificationsAsRead, onMakrkNotificationsAsRead } from 'core/layouts/main/services/notificationsService';
import { Notification } from 'core/layouts/main/models/notificationModel';
import { getNotificationLink } from './notificationLink';

// components
import Icon from 'utils/icon';
import NotificationItem from 'components/notification/notificationItem';
import useAblyChannel from 'core/hooks/useAblyChannel';
import { NOTIFICATIONS_UPDATED, NotificationsUpdatedEvent, notificationCompanyUserChannel } from 'core/layouts/main/models/notificationEvents';
import NoData from 'components/noData/noData';

interface NotificationDrawerProps {
	open: boolean;
	unreadCount: number;
	userCompanyId: number;
	hasActiveSubscription: boolean;
	onClose: () => void;
	onUnreadCountChange: (count: number) => void;
}

const NotificationDrawer: FunctionComponent<NotificationDrawerProps> = ({
	open,
	userCompanyId,
	hasActiveSubscription,
	unreadCount,
	onClose,
	onUnreadCountChange,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [notifications, setNotifications] = useState<Notification[]>([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isMarkingAll, setIsMarkingAll] = useState(false);
	const [showMarkedCheck, setShowMarkedCheck] = useState(false);
	const markedCheckTimeout = useRef<ReturnType<typeof setTimeout>>();

	// the list is loaded once, then kept in sync by the realtime event
	useEffect(() => {
		getNotificationsHandler(1);

		return () => clearTimeout(markedCheckTimeout.current);
	}, []);

	useAblyChannel<NotificationsUpdatedEvent>(notificationCompanyUserChannel(userCompanyId), NOTIFICATIONS_UPDATED, (data) => {
		// a higher unread count means a new notification arrived rather than one being read
		const { last_notification } = data;
		if (!open && last_notification && !last_notification.is_read && data.unread_count > unreadCount) {
			NotificationService.info(
				(isRTL() ? last_notification.translated_title?.ar : last_notification.translated_title?.en) ?? last_notification.title,
				(isRTL() ? last_notification.translated_message?.ar : last_notification.translated_message?.en) ?? last_notification.message,
			);
		}

		onUnreadCountChange(data.unread_count);
		setNotifications(data.notifications.notifications);
		setPage(1);
		setHasMore(!!data.notifications.pagination?.next_page_url);
	});

	// handlers
	const getNotificationsHandler = async (pageNumber: number) => {
		try {
			if (!hasActiveSubscription) return;

			pageNumber === 1 ? setIsLoading(true) : setIsLoadingMore(true);

			const response = await onGetNotifications(pageNumber);
			// new notifications shift the pages, so skip any the list already has
			setNotifications((prev) => {
				if (pageNumber === 1) return response.notifications;
				const loadedIds = new Set(prev.map((notification) => notification.id));
				return [...prev, ...response.notifications.filter((notification) => !loadedIds.has(notification.id))];
			});
			setPage(pageNumber);
			setHasMore(!!response.pagination?.next_page_url);
		} catch (error: any) {
			const message = error?.response?.data?.message || t('COMMON.FETCH_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		} finally {
			setIsLoading(false);
			setIsLoadingMore(false);
		}
	};

	const onScroll = (e: UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
		if (hasMore && !isLoading && !isLoadingMore && scrollHeight - scrollTop - clientHeight < 50) {
			getNotificationsHandler(page + 1);
		}
	};

	const onNotificationClick = async (notification: Notification) => {
		if (!notification.is_read) {
			try {
				await onMakrkNotificationsAsRead(notification.id);
				setNotifications((prev) => prev.map((item) => (item.id === notification.id ? { ...item, is_read: true } : item)));
				onUnreadCountChange(Math.max(unreadCount - 1, 0));
			} catch (error) {}
		}

		const link = getNotificationLink(notification);
		if (link) {
			onClose();
			navigate(link);
		}
	};

	const onMarkAllAsRead = async () => {
		try {
			setIsMarkingAll(true);
			await onMakrkAllNotificationsAsRead();
			setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
			onUnreadCountChange(0);

			setShowMarkedCheck(true);
			if (markedCheckTimeout.current) clearTimeout(markedCheckTimeout.current);
			markedCheckTimeout.current = setTimeout(() => setShowMarkedCheck(false), 3000);
		} catch (error: any) {
			const message = error?.response?.data?.message || t('COMMON.FETCH_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		} finally {
			setIsMarkingAll(false);
		}
	};

	return (
		<Drawer
			open={open}
			closeIcon={null}
			onClose={onClose}
			placement={isRTL() ? 'left' : 'right'}
			className="notification-drawer"
			width={400}
			title={
				<Flex justify="space-between" align="flex-start">
					<div>
						<h4 className="mb-0">{t('COMMON.NOTIFICATIONS')}</h4>
						<Button
							type="link"
							className="p-0 h-auto fw-semibold"
							size="middle"
							loading={isMarkingAll}
							icon={showMarkedCheck && <Icon name="greenCheck" width={14} />}
							onClick={onMarkAllAsRead}>
							{t('COMMON.MARK_ALL_AS_READ')}
						</Button>
					</div>
					<Button type="link" className="p-0" onClick={onClose}>
						<Icon name="close" width={15} height={15} color="#555" />
					</Button>
				</Flex>
			}>
			<div style={{ height: '100%', overflowY: 'auto' }} onScroll={onScroll}>
				{isLoading ? (
					[100, 120, 90, 140, 80].map((key, index) => (
						<Skeleton.Button key={index} active block style={{ width: '100%', height: key, marginBottom: 5 }} />
					))
				) : notifications.length === 0 ? (
					<Flex justify="center" align="center" className="pt-5">
						<NoData message={t('COMMON.NO_NOTIFICATIONS')} />
					</Flex>
				) : (
					notifications.map((notification) => <NotificationItem key={notification.id} notification={notification} onClick={onNotificationClick} />)
				)}

				{isLoadingMore && (
					<Flex justify="center" className="py-3">
						<Spin />
					</Flex>
				)}
			</div>
		</Drawer>
	);
};

export default NotificationDrawer;
